import React, { useEffect, useState } from "react";
import SubmitButton from "../generic/SubmitButton";
import InputField from "../generic/InputField";
import AlertBanner from "../generic/AlertBanner";
import Modal from "../generic/Modal";
import MaterialTypeDropdown from "../shared/MaterialTypeDropdown";
import SizeDropdown from "../shared/SizeDropdown";
import QuantityUnitDropdown from "../shared/QuantityUnitDropdown";
import { MESSAGE_TYPES, Messages, MessageType } from "../../constants/Messages";
import { updateMaterialRecord } from "../../services/MaterialAPI";
import { InventoryRecord } from "../../types/InventoryItem";

interface EditMaterialItemModalProps {
    currentItem: InventoryRecord;
    onClose: () => void;
    onSubmit: (newItem: InventoryRecord) => Promise<void>;
}

const EditMaterialItemModal: React.FC<EditMaterialItemModalProps> = ({ currentItem, onClose, onSubmit }) => {
    const [ item, setItem ] = useState<InventoryRecord>({ ...currentItem });
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ alert, setAlert ] = useState<{ title?: string; message: string; type: MessageType } | null>(null);

    // Sync the state with currentItem if it changes
    useEffect(() => {
        setItem({ ...currentItem });
    }, [ currentItem ]);

    // Check if the form is valid
    const isFormValid = Boolean(
        item.material_type_id &&
        item.material_size_id &&
        item.color_name &&
        item.quantity_unit,
    );

    // Check if the form values differ from the original item
    const isChanged = JSON.stringify(item) !== JSON.stringify(currentItem);

    const handleItemChange = (field: keyof InventoryRecord, value: any) => {
        setItem((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (!item.material_type_id || !item.material_size_id || !item.color_name || !item.quantity_unit) {
            setAlert({
                message: Messages.ERROR_ALL_FIELDS_REQUIRED,
                type: MESSAGE_TYPES.ERROR,
            });
            return;
        }

        if (isSubmitting) return; // Prevent double submissions

        setIsSubmitting(true);
        try {
            const response = await updateMaterialRecord(currentItem.id, item);
            if (response.success) {
                setAlert({
                    message: Messages.SUCCESS_UPDATE_RECORD,
                    type: MESSAGE_TYPES.SUCCESS,
                });
                await onSubmit(response.record); // Controlled submission
            }
        } catch (error: any) {
            console.error("Error updating material item:", error);
            setAlert({
                title: `${Messages.ERROR_UPDATE_RECORD}`,
                message: error.message || Messages.ERROR_UPDATE_RECORD,
                type: MESSAGE_TYPES.ERROR,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose}>
            <h2 className="text-lg font-semibold mb-4">Edit Material Record</h2>

            {alert && (
                <AlertBanner
                    message={alert.message}
                    message_type={alert.type}
                    title={alert.title}
                    onClose={() => setAlert(null)}
                />
            )}

            <MaterialTypeDropdown
                value={item.material_type_id}
                onChange={(value) => handleItemChange("material_type_id", value)}
            />

            <SizeDropdown
                selectedSize={item.material_size_id}
                onSelectSize={(value) => handleItemChange("material_size_id", value)}
            />

            {item.material_type_id && item.material_size_id && (
                <>
                    <InputField
                        type="text"
                        value={item.color_name || ""}
                        onChange={(value) => handleItemChange("color_name", value)}
                        label="Color Name"
                        placeholder="Enter color name"
                        required
                    />

                    <InputField
                        type="text"
                        value={item.color_code || ""}
                        onChange={(value) => handleItemChange("color_code", value)}
                        label="Color Code"
                        placeholder="Enter color code"
                        tooltip="If you don't provide a color code, we will automatically assign a unique, normalized color code for you."
                    />

                    <InputField
                        type="text"
                        value={item.material_description || ""}
                        onChange={(value) => handleItemChange("material_description", value)}
                        label="Material Description"
                        placeholder="Enter material description"
                        tooltip="Provide any additional details about the material, such as unique features, usage guidelines, or specifications."
                    />

                    <QuantityUnitDropdown
                        value={item.quantity_unit || ""}
                        onChange={(value) => handleItemChange("quantity_unit", value)}
                    />

                    <InputField
                        type="number"
                        step="0.01"
                        value={String(item.quantity || 0)}
                        onChange={(value) => handleItemChange("quantity", parseFloat(value.toString()) || 0)}
                        label="Quantity"
                        placeholder="Enter quantity"
                    />

                    <InputField
                        type="number"
                        step="0.01"
                        value={String(item.average_price_per_unit || "")}
                        onChange={(value) => handleItemChange("average_price_per_unit", parseFloat(value.toString()) || 0)}
                        label="Unit Price"
                        placeholder="Enter unit price"
                    />
                </>
            )}

            <div className="flex justify-end mt-4 gap-2">
                <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-700">
                    Cancel
                </button>

                <SubmitButton
                    isLoading={isSubmitting}
                    disabled={!isFormValid || !isChanged || isSubmitting}
                    onClick={handleSubmit}
                >
                    Submit
                </SubmitButton>
            </div>
        </Modal>
    );
};

export default EditMaterialItemModal;
